<!--
  Structure mirrors the mobile `fui-post-card`: avatar + content make up the tappable
  block, and the meta row sits OUTSIDE it. That split is not cosmetic — the meta row holds
  the reaction buttons, and interactive elements cannot legally nest inside an <a> (the
  browser would follow the link on every reaction click).

  `[id]` is the scroll anchor the feed uses to put you back on this row when you return
  from the post detail — see `community.component.postsLoaded()`. `<myp-comment>` carries
  the same id for the detailed view.
-->
<div class="post-list-item" [id]="post?.id">
  <!--
    A real href (crawlers, middle-click, copy-link-address) but NOT a routerLink: routerLink
    installs its own click handler that navigates regardless of our `preventDefault`, so with
    the pane open every row click also ran a full router navigation — which re-ran the
    community shell's init and stuck the feed's skeleton loaders on. `open` handles the
    click, and lets the browser have it untouched when a modifier says "new tab".
  -->
  <!-- One layout with or without a subject: the subject (when there is one) leads, then a
       byline — small avatar, author, time — then the body, all full width with no avatar
       column. With a subject, the subject carries the unread highlight and the author is
       quieter; without, the byline leads (see `li.community-post-unread` in _community.scss). -->
  <a
    class="post-list-item__tappable post-list-item__tappable--stacked"
    [class.post-list-item__tappable--subject-first]="hasSubject"
    [attr.href]="detailHref"
    [attr.aria-current]="current ? 'true' : null"
    (click)="open($event)"
  >
    <div class="post-list-item__content">
      @if (unread) {
        <span class="sr-only">{{ "com.post_unread" | translate }}</span>
      }
      @if (hasSubject) {
        <ng-container *ngTemplateOutlet="subjectTpl"></ng-container>
      }
      <div class="post-list-item__head post-list-item__head--byline">
        <span class="post-list-item__byline">
          <ng-container *ngTemplateOutlet="avatarTpl"></ng-container>
          <span class="post-list-item__author">{{ authorName }}</span>
        </span>
        <ng-container *ngTemplateOutlet="agoTpl"></ng-container>
      </div>
      <ng-container *ngTemplateOutlet="bodyTpl"></ng-container>
    </div>
  </a>

  <!-- Community and channels, as chips. Outside the link like the meta row: a chip is its own
       control, and switches the feed rather than opening the post (as on mobile). -->
  @if (tags?.length) {
    <div class="post-list-item__tags post-list-item__tags--flush">
      @for (tag of tags; track tag.kind + ":" + tag.communityStringId + ":" + (tag.channelStringId || "")) {
        @if (tagsInteractive) {
          <button type="button" class="community-post-tag" [class.community-post-tag--community]="tag.kind === 'community'" (click)="onTag(tag, $event)">
            <ng-container *ngTemplateOutlet="tagContent; context: { $implicit: tag }"></ng-container>
          </button>
        } @else {
          <span class="community-post-tag" [class.community-post-tag--community]="tag.kind === 'community'">
            <ng-container *ngTemplateOutlet="tagContent; context: { $implicit: tag }"></ng-container>
          </span>
        }
      }
    </div>
  }

  <!-- Flush with the content: rows have no avatar column to clear. -->
  <div class="post-list-item__meta post-list-item__meta--flush">
    <a class="post-list-item__stat" [attr.href]="detailHref" (click)="open($event)">
      <fa-icon icon="comment" aria-hidden="true"></fa-icon>
      <span>{{ postModel?.replyCount ?? replies?.length ?? 0 }}</span>
      <span class="sr-only">{{ "com.replies_open_post" | translate }}</span>
    </a>

    <!-- A moderator's pinned announcement keeps the thumbtack and is display-only; your own
         saved post (`pins`) is a bookmark you can click to unsave. -->
    @if (isAdminPinned) {
      <span class="post-list-item__stat post-list-item__stat--pinned">
        <fa-icon icon="thumbtack" aria-hidden="true"></fa-icon>
        <span class="sr-only">{{ "com.post_pinned_label" | translate }}</span>
      </span>
    } @else if (isSaved) {
      <button
        type="button"
        class="post-list-item__stat post-list-item__stat--pinned post-list-item__stat--button"
        (click)="unsave($event)"
        [attr.aria-label]="'com.unsave_post' | translate"
        [ngbTooltip]="'com.unsave_post' | translate"
      >
        <fa-icon icon="bookmark" aria-hidden="true"></fa-icon>
      </button>
    }

    <!--
      The shared reaction control — same chips, same picker, same add button as the detailed
      view, so reacting from a list row writes through the one path in
      `CommunityReactionService`. It emits bare <li>s, hence the list-inline wrapper.
    -->
    @if (!compact) {
      <ul class="list-inline post-list-item__reactions mb-0">
        <myp-reaction-control [comment]="post" (reactionChange)="onReactionChange($event)" (viewProfile)="onViewProfile($event)"></myp-reaction-control>
      </ul>
    }
  </div>
</div>

<!-- DENG-846 hover card, same as the detailed view's post avatar. Suppressed for signed-out
     readers by the directive itself — `user_profiles` is closed to them. -->
<ng-template #avatarTpl>
  <div class="post-list-item__avatar" [mypUserHoverCard]="post?.author?.id" [style.background-color]="authorPhoto ? 'transparent' : avatarColor">
    @if (authorPhoto) {
      <!-- alt="": the author's name is right beside it, inside the same link. -->
      <img [src]="authorPhoto" alt="" />
    } @else {
      <span aria-hidden="true">{{ initials }}</span>
    }
  </div>
</ng-template>

<!-- Last activity, not creation date: scanning a feed, "this thread was touched an hour ago"
     is the useful fact. Labelled only when a reply is what made it recent, since on a thread
     with no replies the two are the same date. `title` keeps the original post time reachable.
     `: true` is the pipe's chatTimestamp mode — "5:45 AM" today, "Yesterday at 8:36 PM"
     yesterday, "MMM Do" beyond. Same call the detailed view makes, and what the mobile card
     shows. Both dates are pre-resolved in the component; see why there. -->
<ng-template #agoTpl>
  <span class="post-list-item__ago" [attr.title]="hasLaterActivity ? 'Posted ' + (createdDate | date: 'MMM Do [at] h:mma' : true) : null">
    @if (hasLaterActivity) {
      <span class="post-list-item__ago-label">Latest activity</span>
    }
    {{ activityDate | date: "MMM Do [at] h:mma" : true }}
  </span>
</ng-template>

<ng-template #tagContent let-tag>
  @if (tag.kind === "community") {
    <span class="community-post-tag__dot" [style.background]="tag.accent || null" aria-hidden="true"></span>
    {{ tag.label }}
  } @else {
    #{{ tag.label }}
  }
</ng-template>

<ng-template #subjectTpl>
  @if (subjectHtml) {
    <div class="post-list-item__subject" [innerHTML]="subjectHtml"></div>
  } @else if (subject) {
    <div class="post-list-item__subject">{{ subject }}</div>
  }
</ng-template>

<ng-template #bodyTpl>
  <div class="post-list-item__body-row">
    @if (bodyHtml) {
      <div class="post-list-item__body" [innerHTML]="bodyHtml"></div>
    } @else if (bodyPreview) {
      <div class="post-list-item__body">{{ bodyPreview }}</div>
    }
    @if (thumbUrl || thumbIcon) {
      <div class="post-list-item__thumb" aria-hidden="true">
        @if (thumbUrl) {
          <img [src]="thumbUrl" alt="" />
        } @else if (thumbIcon === "play") {
          <fa-icon icon="play"></fa-icon>
        } @else if (thumbIcon === "video") {
          <fa-icon icon="video"></fa-icon>
        } @else {
          <fa-icon icon="file"></fa-icon>
        }
      </div>
    }
  </div>
</ng-template>
